import { useEffect, useState } from "react";
import { headerData } from "@/lib/constants";
import { Menu, Car, Users, MessageCircle, Phone, Mail, FileText, Shield, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-2 hidden md:block">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Phone className="w-4 h-4" />
              <span>+91 7058289819</span>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>support@timelyyy.com</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
              <MessageCircle className="w-3 h-3 mr-1" />
              WhatsApp Integration
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200"
            : "bg-white/90 backdrop-blur-sm"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            {/* Logo Section */}
            <Link to={"/"} className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-green-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              </div>
              <div>
                <span className="font-bold text-2xl bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                  Timelyyy
                </span>
                <p className="text-xs text-gray-500 -mt-1">Vehicle Wash Booking</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {headerData.headerLinks.map((link) => (
                <Link
                  to={link.href}
                  key={link.name}
                  className="relative group text-gray-700 hover:text-blue-600 font-medium transition-all duration-300"
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-green-600 group-hover:w-full transition-all duration-300"></span>
                </Link>
              ))}
            </nav>

            {/* Action Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link to="/onboard-vendor">
                <Button variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                  <Users className="w-4 h-4 mr-2" />
                  Join as Vendor
                </Button>
              </Link>
              <Link to="/booking">
                <Button className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white shadow-lg hover:shadow-xl transition-all duration-300">
                  <Car className="w-4 h-4 mr-2" />
                  Book Now
                </Button>
              </Link>
            </div>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button size="icon" variant="ghost" className="lg:hidden">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent className="w-80 overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-green-500 rounded-lg flex items-center justify-center">
                      <Car className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-bold text-xl bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                        Timelyyy
                      </div>
                      <div className="text-xs text-gray-500">Vehicle Wash Booking</div>
                    </div>
                  </SheetTitle>
                </SheetHeader>

                <div className="mt-8 space-y-6">
                  {/* Mobile Navigation Links */}
                  <nav className="space-y-4">
                    {headerData.headerLinks.map((link) => (
                      <Link
                        to={link.href}
                        key={link.name}
                        onClick={() => setIsOpen(false)}
                        className="flex items-center space-x-3 text-lg font-medium text-gray-700 hover:text-blue-600 transition-colors py-2"
                      >
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        {link.name}
                      </Link>
                    ))}
                  </nav>

                  <Separator className="my-4" />

                  {/* Legal Links Section */}
                  <div className="space-y-3">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Legal & Policies</h3>
                    <div className="space-y-2">
                      <Link
                        to="/privacy"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center space-x-3 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors py-2"
                      >
                        <Shield className="w-4 h-4" />
                        Privacy Policy
                      </Link>
                      <Link
                        to="/tnc"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center space-x-3 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors py-2"
                      >
                        <FileText className="w-4 h-4" />
                        Terms & Conditions
                      </Link>
                      <Link
                        to="/refund"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center space-x-3 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors py-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Refund Policy
                      </Link>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  {/* Mobile Action Buttons */}
                  <div className="space-y-3">
                    <Link to="/onboard-vendor" className="block" onClick={() => setIsOpen(false)}>
                      <Button variant="outline" className="w-full justify-start">
                        <Users className="w-4 h-4 mr-2" />
                        Join as Vendor
                      </Button>
                    </Link>
                    <Link to="/booking" className="block" onClick={() => setIsOpen(false)}>
                      <Button className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white">
                        <Car className="w-4 h-4 mr-2" />
                        Book Your Wash
                      </Button>
                    </Link>
                  </div>

                  {/* Contact Info */}
                  <div className="pt-6 border-t space-y-3">
                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>+91 7058289819</span>
                    </div>
                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      <span>support@timelyyy.com</span>
                    </div>
                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                      <MessageCircle className="w-4 h-4" />
                      <span>WhatsApp Integration</span>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
};

